// src/pages/PatientWalletPage.jsx
// Route: /patient/wallet
//
// Patient-facing wallet: balance, top-up (Razorpay), transaction history.
// No withdrawal — this wallet only ever pays for Previu lab tests/
// appointments, or gets refunded back into itself on cancellation.
// Uses PatientAppShell for navigation chrome, walletAPI (axios instance,
// never raw fetch — FIGMA_TO_CODE_CHECKLIST.md Section 6) for all calls.

import React, { useEffect, useState, useCallback } from 'react';
import { PatientAppShell } from '../components/PatientLayout';
import WalletBillingTabs from '../components/WalletBillingTabs';
import { usePatient } from '../context/PatientContext';
import { useToast } from '../context/ToastContext';
import { walletAPI } from '../services/api';
import { openRazorpayCheckout } from '../services/razorpay';

const MIN_TOPUP_RUPEES = 500;

const PRESET_AMOUNTS = [500, 1000, 2000, 5000];

function formatRupees(minorUnits) {
  return (minorUnits / 100).toLocaleString('en-IN', {
    style: 'currency', currency: 'INR', maximumFractionDigits: 0,
  });
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// 28 Jul 2026 — updated. Backend's "Transaction History" endpoint now
// returns the UNIFIED ledger (Deepu, 28 Jul 2026: same screen/endpoint for
// patient and navigator) — wallet types are now prefixed WALLET_, and two
// new synthetic types (RAZORPAY_LAB_ORDER/RAZORPAY_APPOINTMENT) represent
// the gateway portion of a payment that never touched the wallet at all.
const TXN_LABELS = {
  WALLET_CREDIT_TOPUP:      { label: 'Added money',        sign: '+', color: 'var(--green)' },
  WALLET_CREDIT_REFUND:     { label: 'Refund',              sign: '+', color: 'var(--green)' },
  WALLET_CREDIT_ADJUSTMENT: { label: 'Adjustment credit',   sign: '+', color: 'var(--green)' },
  WALLET_DEBIT_LAB_ORDER:   { label: 'Lab test order',      sign: '−', color: 'var(--red)' },
  WALLET_DEBIT_APPOINTMENT: { label: 'Doctor appointment',  sign: '−', color: 'var(--red)' },
  WALLET_DEBIT_ADJUSTMENT:  { label: 'Adjustment debit',    sign: '−', color: 'var(--red)' },
  // Gateway-only — paid entirely (or partly) via Razorpay, never debited
  // from the wallet, so there's no running balance to show for these rows.
  RAZORPAY_LAB_ORDER:       { label: 'Lab test order (card/UPI)',     sign: '−', color: 'var(--red)' },
  RAZORPAY_APPOINTMENT:     { label: 'Doctor appointment (card/UPI)', sign: '−', color: 'var(--red)' },
};

export default function PatientWalletPage() {
  const { patient } = usePatient();
  const { toast } = useToast();

  const [balanceMinorUnits, setBalanceMinorUnits] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [txnPage, setTxnPage] = useState(0);
  const [txnTotalPages, setTxnTotalPages] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [topupAmount, setTopupAmount] = useState('');
  const [topupInFlight, setTopupInFlight] = useState(false);

  const loadWallet = useCallback(async () => {
    if (!patient?.id) return;
    setLoading(true);
    setError(false);
    try {
      const [balanceRes, txnRes] = await Promise.all([
        walletAPI.getBalance(patient.id),
        walletAPI.getTransactions(patient.id, 0, 20),
      ]);
      setBalanceMinorUnits(balanceRes.data.balanceMinorUnits);
      setTransactions(txnRes.data.transactions || []);
      setTxnPage(0);
      setTxnTotalPages(txnRes.data.totalPages ?? 1);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [patient?.id]);

  // 28 Jul 2026 — new. The unified ledger can genuinely be longer now
  // (gateway-only entries added on top of wallet ones), so a flat 20-row
  // list with no way to see more wasn't going to hold up.
  const loadMoreTransactions = async () => {
    if (!patient?.id || loadingMore || txnPage + 1 >= txnTotalPages) return;
    setLoadingMore(true);
    try {
      const nextPage = txnPage + 1;
      const res = await walletAPI.getTransactions(patient.id, nextPage, 20);
      setTransactions(prev => [...prev, ...(res.data.transactions || [])]);
      setTxnPage(nextPage);
    } catch {
      toast.error('Could not load more transactions.');
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => { loadWallet(); }, [loadWallet]);

  // 27 Jul 2026 — fix: after a UPI/netbanking redirect (leaves the browser
  // tab, comes back), some mobile browsers discard the page's JS context and
  // reload fresh — the in-page Razorpay `handler` callback that would have
  // called loadWallet() never gets a chance to fire, since the JS execution
  // that was waiting on it is gone. The wallet DOES get credited correctly
  // server-side (that's why a manual refresh shows the right balance) — this
  // just makes sure the UI catches up on its own instead of requiring one.
  // Covers both: the tab regaining visibility (mobile: switching back from a
  // UPI app) and the window regaining focus (desktop: alt-tabbing back).
  useEffect(() => {
    const handleVisibilityOrFocus = () => {
      if (document.visibilityState === 'visible') {
        loadWallet();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityOrFocus);
    window.addEventListener('focus', handleVisibilityOrFocus);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityOrFocus);
      window.removeEventListener('focus', handleVisibilityOrFocus);
    };
  }, [loadWallet]);

  const handleTopup = async (rupees) => {
    if (!patient?.id) return;
    if (rupees < MIN_TOPUP_RUPEES) {
      toast.error(`Minimum top-up is ₹${MIN_TOPUP_RUPEES}`);
      return;
    }
    const amountMinorUnits = Math.round(rupees * 100);
    setTopupInFlight(true);
    try {
      const { data: order } = await walletAPI.createTopupOrder(patient.id, amountMinorUnits);

      const result = await openRazorpayCheckout({
        amount: amountMinorUnits,
        currency: 'INR',
        name: 'Previu Health',
        description: 'Wallet top-up',
        orderId: order.razorpayOrderId,
        prefill: {
          name: `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
          contact: patient.phone,
          email: patient.email,
        },
      });

      if (!result) {
        // User dismissed the modal — not an error, just no-op.
        setTopupInFlight(false);
        return;
      }

      await walletAPI.verifyTopup(patient.id, {
        razorpayOrderId: result.razorpay_order_id,
        razorpayPaymentId: result.razorpay_payment_id,
        razorpaySignature: result.razorpay_signature,
        amountMinorUnits,
      });

      toast.success(`₹${rupees} added to your wallet`);
      setTopupAmount('');
      await loadWallet();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Top-up failed. Please try again.');
    } finally {
      setTopupInFlight(false);
    }
  };

  return (
    <PatientAppShell breadcrumbs={[{ label: 'Wallet & Billing', to: '/patient/wallet' }, { label: 'Wallet' }]}>
      <div style={{
        maxWidth: 560, margin: '0 auto',
        padding: '24px 0 48px',
        fontFamily: 'var(--font-body)',
      }}>
        <WalletBillingTabs />

        <div style={{
          fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
          letterSpacing: '.1em', color: 'var(--text-muted)', marginBottom: 4,
        }}>
          Previu Wallet
        </div>
        <div style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 24 }}>
          Balance &amp; top-up
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)', fontSize: 14 }}>
            Loading your wallet…
          </div>
        )}

        {!loading && error && (
          <div style={{
            background: 'var(--red-bg)', border: '1px solid var(--red)',
            borderRadius: 'var(--radius-lg)', padding: '20px 24px', color: 'var(--red)', fontSize: 13,
          }}>
            Couldn't load your wallet. Please try again.
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Balance card */}
            <div style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)', padding: '28px 24px', marginBottom: 20,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                Available balance
              </div>
              <div style={{
                fontSize: 40, fontWeight: 500, lineHeight: 1,
                fontFamily: 'var(--font-display)', color: 'var(--text-primary)', marginBottom: 4,
              }}>
                {formatRupees(balanceMinorUnits || 0)}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Used for lab tests and doctor appointments — not withdrawable.
              </div>
            </div>

            {/* Top-up */}
            <div style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)', padding: '20px 24px', marginBottom: 20,
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 12 }}>
                Add money
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
                {PRESET_AMOUNTS.map((amt) => (
                  <button
                    key={amt}
                    type="button"
                    disabled={topupInFlight}
                    onClick={() => handleTopup(amt)}
                    style={{
                      flexShrink: 0, whiteSpace: 'nowrap',
                      padding: '8px 16px', borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)', background: 'var(--bg-subtle)',
                      color: 'var(--text-primary)', fontSize: 13, fontWeight: 500,
                      cursor: topupInFlight ? 'default' : 'pointer', opacity: topupInFlight ? 0.6 : 1,
                    }}
                  >
                    ₹{amt}
                  </button>
                ))}
              </div>

              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <input
                  type="number"
                  min={MIN_TOPUP_RUPEES}
                  step={1}
                  placeholder={`Custom amount (min ₹${MIN_TOPUP_RUPEES})`}
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(e.target.value)}
                  disabled={topupInFlight}
                  style={{
                    flex: '1 1 180px', minWidth: 0, padding: '9px 12px',
                    borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                    fontSize: 13, fontFamily: 'var(--font-body)', color: 'var(--text-primary)',
                  }}
                />
                <button
                  type="button"
                  disabled={topupInFlight || !topupAmount}
                  onClick={() => handleTopup(Number(topupAmount))}
                  style={{
                    flexShrink: 0, whiteSpace: 'nowrap',
                    padding: '9px 18px', borderRadius: 'var(--radius-sm)', border: 'none',
                    background: 'var(--brand-dark)', color: 'var(--text-on-brand-dark)',
                    fontSize: 13, fontWeight: 600,
                    cursor: (topupInFlight || !topupAmount) ? 'default' : 'pointer',
                    opacity: (topupInFlight || !topupAmount) ? 0.6 : 1,
                  }}
                >
                  {topupInFlight ? 'Processing…' : 'Add'}
                </button>
              </div>
            </div>

            {/* Transaction history */}
            <div style={{
              background: 'var(--bg-surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-lg)', padding: '20px 24px',
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 14 }}>
                Transaction History
              </div>

              {transactions.length === 0 && (
                <div style={{ fontSize: 13, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  No transactions yet.
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {transactions.map((txn) => {
                  const meta = TXN_LABELS[txn.type] || { label: txn.type, sign: '', color: 'var(--text-muted)' };
                  return (
                    <div key={txn.id} style={{
                      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10,
                      padding: '10px 0', borderBottom: '1px solid var(--bg-subtle)',
                    }}>
                      <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
                          {meta.label}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {formatDate(txn.createdAt)}
                        </div>
                      </div>
                      <div style={{
                        fontSize: 14, fontWeight: 600, color: meta.color,
                        flexShrink: 0, whiteSpace: 'nowrap',
                      }}>
                        {meta.sign}{formatRupees(txn.amountMinorUnits)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {txnPage + 1 < txnTotalPages && (
                <button
                  type="button"
                  onClick={loadMoreTransactions}
                  disabled={loadingMore}
                  style={{
                    marginTop: 14, width: '100%', padding: '9px 12px',
                    borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)',
                    background: 'var(--bg-subtle)', color: 'var(--text-primary)',
                    fontSize: 13, fontWeight: 500, cursor: loadingMore ? 'default' : 'pointer',
                    opacity: loadingMore ? 0.6 : 1,
                  }}
                >
                  {loadingMore ? 'Loading…' : 'Load more'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </PatientAppShell>
  );
}
